import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtMoney(n) { return `$${(n || 0).toFixed(2)}`; }
function Card({ children, style }) {
  return <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, ...style }}>{children}</div>;
}

const GRID = '2fr 1.8fr 70px 100px 100px 120px';

export default function Customers() {
  const { restaurant } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');

  useEffect(() => {
    if (!restaurant?.id) return;
    const load = async () => {
      setLoading(true);
      const data = await portalFetch('/customers');
      if (data) setCustomers(data.customers || []);
      setLoading(false);
    };
    load();
  }, [restaurant?.id]);

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) ||
           c.email.toLowerCase().includes(q) ||
           c.phone.toLowerCase().includes(q);
  });

  const totalRevenue  = customers.reduce((s, c) => s + c.totalSpend, 0);
  const totalOrders   = customers.reduce((s, c) => s + c.orders, 0);
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const bothCustomers = customers.filter(c => c.orders > 0 && c.reservations > 0).length;

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900 }}>Customers</h1>
        <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 2 }}>
          Everyone who has ordered or reserved — deduplicated by email, built automatically from every order and reservation
        </p>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total customers',  value: customers.length },
          { label: 'Order + reserved', value: bothCustomers },
          { label: 'Total revenue',    value: fmtMoney(totalRevenue) },
          { label: 'Avg order value',  value: fmtMoney(avgOrderValue) },
        ].map(({ label, value }) => (
          <Card key={label} style={{ padding: '1.2rem 1.4rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>{label}</div>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.7rem', fontWeight: 900, color: 'var(--orange)' }}>{value}</div>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, or phone..."
          style={{ width: '100%', maxWidth: 400, padding: '0.75rem 1rem', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.9rem', outline: 'none', color: 'var(--ink)' }}
        />
      </div>

      <Card>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>Building your customer database...</div>
        ) : customers.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>👥</div>
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Customer data will appear here once orders or reservations come in.</p>
          </div>
        ) : (
          <div>
            {/* Header row */}
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '1rem', padding: '0.75rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--warm)', borderRadius: '12px 12px 0 0' }}>
              {['Guest', 'Contact', 'Orders', 'Reservations', 'Spent', 'Last Activity'].map(h => (
                <div key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
              ))}
            </div>

            {/* Data rows */}
            {filtered.map((c, i) => (
              <div
                key={i}
                style={{ display: 'grid', gridTemplateColumns: GRID, gap: '1rem', padding: '0.9rem 1.4rem', borderBottom: '1px solid var(--warm)', alignItems: 'center', fontSize: '0.875rem' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                {/* Guest — name + badge on separate lines */}
                <div>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  {c.orders > 0 && c.reservations > 0 && (
                    <span style={{ display: 'inline-block', marginTop: 3, fontSize: '0.68rem', background: 'var(--green-bg)', color: 'var(--green-tx)', padding: '0.1rem 0.45rem', borderRadius: 10, fontWeight: 700 }}>
                      Order + Res
                    </span>
                  )}
                  {c.orders > 1 && !c.reservations && (
                    <span style={{ display: 'inline-block', marginTop: 3, fontSize: '0.68rem', background: 'var(--warm)', color: 'var(--orange)', padding: '0.1rem 0.45rem', borderRadius: 10, fontWeight: 700, border: '1px solid var(--border)' }}>
                      Repeat
                    </span>
                  )}
                </div>

                {/* Contact — email + phone stacked */}
                <div style={{ fontSize: '0.82rem', color: 'var(--mid)' }}>
                  {c.email !== '—' && (
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</div>
                  )}
                  {c.phone !== '—' && (
                    <div style={{ marginTop: 2 }}>{c.phone}</div>
                  )}
                </div>

                {/* Orders */}
                <div style={{ fontWeight: 600, textAlign: 'center' }}>
                  {c.orders > 0 ? c.orders : '—'}
                </div>

                {/* Reservations */}
                <div style={{ fontWeight: 600, textAlign: 'center' }}>
                  {c.reservations > 0 ? c.reservations : '—'}
                </div>

                {/* Spent */}
                <div style={{ fontWeight: 700, color: c.totalSpend > 0 ? 'var(--orange)' : 'var(--muted)' }}>
                  {c.totalSpend > 0 ? fmtMoney(c.totalSpend) : '—'}
                </div>

                {/* Last activity */}
                <div style={{ color: 'var(--muted)' }}>
                  {fmtDate(c.lastActivity || c.lastOrder)}
                </div>
              </div>
            ))}

            {filtered.length === 0 && search && (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.875rem' }}>
                No customers matching "{search}"
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
